import React, { useMemo, useRef, useState } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import { twMerge } from 'tailwind-merge';
import { Card } from './Card';
import { Button } from './Button';
import { useIsMobile } from '../../hooks/useIsMobile';
import { parseDateOnly, formatDateOnlyFromDate } from '../../utils/dateUtils';

export interface DateRangePickerProps {
  /** "YYYY-MM-DD", or '' when unset. */
  startDate: string;
  endDate: string;
  /** Fires with both dates on every selection step (first click sets both to the
   *  same day, matching the pre-existing native-input auto-bump behavior this
   *  replaces -- see TravelScheduleModal's old handleChange). */
  onChange: (startDate: string, endDate: string) => void;
  /** Disable days before today. Only true in Add mode -- Edit mode must allow
   *  editing a trip whose dates are already in the past. */
  disablePast?: boolean;
  /** Days to mark as busy/conflicting (advisory -- the submit-time 409 response
   *  remains the authoritative conflict check; this is a browsing aid only). */
  busyDates?: Set<string>;
  id?: string;
  className?: string;
}

// Day-cell/nav-button sizing is mobile/desktop-specific (mirrors the approved
// mockup's own `.cal-grid.mobile .cal-day { height:46px }` override, with its
// documented reasoning: "aspect-ratio:1 combined with a min-height meant the
// 44px floor was only hit by coincidence... any padding change would have
// silently shrunk touch targets below the 44px rule" -- CLAUDE.md's mobile-
// first "Touch targets >= 44px" rule is a touch/mobile requirement, not a
// desktop-mouse one, so desktop keeps the tighter 36px/28px sizing the two-
// month layout needs to fit comfortably.
function buildDayPickerClassNames(isMobile: boolean) {
  const daySize = isMobile ? 'w-11 h-11' : 'w-9 h-9';
  const navSize = isMobile ? 'w-11 h-11' : 'w-7 h-7';
  return {
    root: 'text-sm',
    // `relative` is load-bearing, not decorative: react-day-picker renders `nav`
    // as a single absolutely-positioned element that's a DIRECT CHILD of this
    // `months` wrapper (confirmed by reading its source -- with no `navLayout`
    // prop set, Nav renders once, before the per-month loop, not nested inside
    // each month's own `month_caption`). Without `relative` here, `nav`'s
    // `absolute inset-x-0 top-0` has no positioned ancestor until several levels
    // further up (the modal's own scroll container), so the prev/next buttons
    // land at the top of the whole modal instead of next to the calendar --
    // functional (still clickable) but effectively invisible/undiscoverable.
    // bug-registry.md, DateRangePicker desktop-nav-positioning entry.
    months: 'relative flex flex-col sm:flex-row gap-4',
    month: 'flex flex-col gap-2',
    month_caption: 'flex items-center justify-center h-9 relative',
    caption_label: 'text-sm font-semibold text-text-primary',
    nav: 'flex items-center justify-between absolute inset-x-0 top-0 h-9 pointer-events-none',
    button_previous:
      `pointer-events-auto inline-flex items-center justify-center ${navSize} rounded-lg text-text-secondary hover:bg-surface hover:text-text-primary disabled:opacity-30 disabled:pointer-events-none`,
    button_next:
      `pointer-events-auto inline-flex items-center justify-center ${navSize} rounded-lg text-text-secondary hover:bg-surface hover:text-text-primary disabled:opacity-30 disabled:pointer-events-none`,
    chevron: 'fill-current w-3.5 h-3.5',
    month_grid: 'border-collapse mt-1',
    weekdays: 'flex',
    weekday: `${daySize} text-xs font-medium text-text-tertiary flex items-center justify-center`,
    week: 'flex',
    day: `relative ${daySize} p-0 text-center align-middle`,
    day_button:
      `${daySize} rounded-full text-sm text-text-primary flex items-center justify-center hover:bg-surface transition-colors`,
    today: '[&>button]:font-bold [&>button]:text-accent',
    selected: '',
    range_start: '[&>button]:bg-accent [&>button]:text-white [&>button]:hover:bg-accent',
    range_end: '[&>button]:bg-accent [&>button]:text-white [&>button]:hover:bg-accent',
    // Tailwind opacity modifiers (bg-accent/15) don't work here -- `accent` is a
    // raw `var(--theme-accent)` CSS variable, not the rgb()-tuple format Tailwind
    // needs to substitute an alpha channel, so bg-accent/15 silently generates no
    // CSS (confirmed live: 0 matching rules in the built stylesheet). Every other
    // translucent-accent usage in this codebase (including this file's own
    // TravelScheduleModal.tsx neighbor) hardcodes the accent's rgb() with an
    // explicit dark: variant instead.
    range_middle: '[&>button]:rounded-none [&>button]:bg-[rgba(184,115,51,0.15)] dark:[&>button]:bg-[rgba(184,115,51,0.25)] [&>button]:hover:bg-[rgba(184,115,51,0.25)] dark:[&>button]:hover:bg-[rgba(184,115,51,0.35)]',
    outside: '[&>button]:text-text-tertiary [&>button]:opacity-40',
    disabled: '[&>button]:text-text-disabled [&>button]:opacity-40 [&>button]:cursor-not-allowed [&>button]:hover:bg-transparent',
    hidden: 'invisible',
  };
}

const busyModifierClassName =
  '[&>button]:bg-[repeating-linear-gradient(45deg,var(--theme-warning-bg)_0_5px,transparent_5px_10px)] [&>button]:after:content-[""] [&>button]:after:absolute [&>button]:after:bottom-0.5 [&>button]:after:left-1/2 [&>button]:after:-translate-x-1/2 [&>button]:after:w-1 [&>button]:after:h-1 [&>button]:after:rounded-full [&>button]:after:bg-[var(--theme-warning)]';

function nightsBetween(start: string, end: string): number {
  if (!start || !end) return 0;
  const ms = parseDateOnly(end).getTime() - parseDateOnly(start).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

/** Adds `delta` months, pinned to the 1st to avoid month-end overflow (e.g. Jan 31 + 1mo skipping to Mar). */
function addMonths(date: Date, delta: number): Date {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + delta);
  return d;
}

const SWIPE_MIN_DISTANCE_PX = 50;
const SWIPE_MAX_VERTICAL_DRIFT_PX = 60;

/**
 * Hotel/airline-style inline range calendar (Spec: travel-date-range-picker
 * mockup, CEO-approved 2026-08-09). Rendered inline rather than as a popover
 * -- TravelScheduleModal is a full-screen `variant="sheet"` on mobile, and a
 * floating popover panel risks clipping/z-index bugs inside a scrollable
 * sheet that an always-visible inline calendar avoids entirely.
 */
export function DateRangePicker({
  startDate,
  endDate,
  onChange,
  disablePast = false,
  busyDates,
  id,
  className,
}: DateRangePickerProps) {
  const isMobile = useIsMobile();
  const dayPickerClassNames = useMemo(() => buildDayPickerClassNames(isMobile), [isMobile]);
  const [today] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  // Controlled so a mobile swipe can move it -- react-day-picker's own prev/next
  // buttons work uncontrolled, but there's otherwise no way to change the
  // displayed month except by clicking them (bug-registry.md, DateRangePicker
  // swipe entry). Initializes to the selected start date's month when one is
  // already set (e.g. Edit mode), matching react-day-picker's own uncontrolled
  // default behavior, so lifting this to controlled state doesn't change what
  // month shows on first render.
  const [month, setMonth] = useState<Date>(() => (startDate ? parseDateOnly(startDate) : today));
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN_DISTANCE_PX || Math.abs(dy) > SWIPE_MAX_VERTICAL_DRIFT_PX) return;
    setMonth((prev) => addMonths(prev, dx < 0 ? 1 : -1));
  };

  const selected: DateRange | undefined = useMemo(() => {
    if (!startDate) return undefined;
    return {
      from: parseDateOnly(startDate),
      to: endDate ? parseDateOnly(endDate) : undefined,
    };
  }, [startDate, endDate]);

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange('', '');
      return;
    }
    const fromStr = formatDateOnlyFromDate(range.from);
    const toStr = range.to ? formatDateOnlyFromDate(range.to) : fromStr;
    onChange(fromStr, toStr);
  };

  const nights = nightsBetween(startDate, endDate);

  return (
    <Card variant="bordered" padding="md" className={twMerge('flex flex-col gap-3', className)} id={id}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm text-text-primary">
          {startDate ? (
            <>
              <span className="font-medium">
                {parseDateOnly(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {endDate && endDate !== startDate && (
                  <>
                    {' – '}
                    {parseDateOnly(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </>
                )}
              </span>
              {nights > 0 && <span className="text-text-secondary"> {'·'} {nights} night{nights === 1 ? '' : 's'}</span>}
              {startDate && (!endDate || endDate === startDate) && (
                <span className="text-text-secondary"> {'·'} pick your return date</span>
              )}
            </>
          ) : (
            <span className="text-text-secondary">Select your travel dates</span>
          )}
        </span>
        {startDate && (
          <Button type="button" variant="text" size="sm" onClick={() => onChange('', '')}>
            Clear dates
          </Button>
        )}
      </div>

      <div
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        <DayPicker
          mode="range"
          numberOfMonths={isMobile ? 1 : 2}
          month={isMobile ? month : undefined}
          onMonthChange={isMobile ? setMonth : undefined}
          selected={selected}
          onSelect={handleSelect}
          disabled={disablePast ? { before: today } : undefined}
          modifiers={busyDates && busyDates.size > 0
            ? { busy: (date: Date) => busyDates.has(formatDateOnlyFromDate(date)) }
            : undefined}
          modifiersClassNames={{ busy: busyModifierClassName }}
          classNames={dayPickerClassNames}
          // Only in the single-month mobile view. In the two-month desktop
          // view (numberOfMonths=2), react-day-picker renders each grid's
          // outside days using the ADJACENT month's real dates -- e.g. the
          // September grid's trailing week shows Oct 1-3 to fill out the
          // row, using the same date objects (and therefore the same
          // aria-labels, e.g. "Saturday, October 3rd, 2026") as the October
          // grid's own real day 3. Two buttons with an identical accessible
          // name is a genuine a11y defect (not just a testability one --
          // caught by Playwright's strict-mode locator, travel-date-range-
          // picker.spec.ts), on top of the visual redundancy of showing the
          // same days twice across the two grids.
          showOutsideDays={isMobile}
        />
      </div>
    </Card>
  );
}

DateRangePicker.displayName = 'DateRangePicker';
